import React, { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Podcast, VoicePreset } from "src/types/coaching/podcast";

import PodcastConfigurations from "../configurations";
import PodcastDetails from "../details";

import { MenuState, PodcastMenuBar, StudioMenuBar } from "../menu";
import { fetchPodcastTranscription, generateAudioForPodcast, generateTranscriptForPodcast, invalidateQuickGeneratePodcastState, quickGeneratePodcast, updatePodcast, updateValidationErrorsForPodcast } from "src/redux/actions/coaching/podcast";

import _ from "lodash";
import cx from "classnames";
import styles from "./studio.module.scss";
import Actions from "src/components/actionbar";
import { assets } from "src/localization/english/keywords";
import { Asset } from "src/types/assets";
import { preparePodcastConfigurationRequest, preparePodcastRequest, validatePodcastRequest } from "../../edit/helper";

interface EditStudioProps {
  podcast: Podcast;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuStateChange: (menuState: MenuState) => void;
}

export interface PodcastEditState {
  assets: Asset[];
  duration?: number;
  voicePreset?: VoicePreset | null;
}

export const EditInStudio: React.FC<EditStudioProps> = ({
  podcast,
  menuState,
  onMenuStateChange
}) => {

  const dispatch = useDispatch();

  const [editState, setEditState] = useState<PodcastEditState>({
    assets: podcast.assets || [],
    duration: podcast.duration || 0,
    voicePreset: podcast.voicePreset
  });

  const quickGeneratingPodcast = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.quickGenerate.generating`, false);
  });

  const transcriptGenerated = useSelector((state: any) => {
    return _.get(state.coaching.podcasts, `edit.generateTranscript.generated`, null);
  });

  useEffect(() => {
    dispatch(fetchPodcastTranscription(podcast.id!));
  }, []);

  useEffect(() => {
    setEditState({
      assets: podcast.assets || [],
      duration: podcast.duration || 0,
      voicePreset: podcast.voicePreset
    });
  }, []);

  useEffect(() => {
    if (quickGeneratingPodcast && transcriptGenerated && podcast.id) {
      dispatch(invalidateQuickGeneratePodcastState(podcast.id!));
    }
  }, [quickGeneratingPodcast, transcriptGenerated]);

  const handleBackClicked = () => {
    onMenuStateChange({
      ...menuState,
      secondaryAction: null,
    });
  };

  const handleChange = (property, value) => {
    setEditState((prev) => {
      return {
        ...prev,
        [property]: value,
      }
    });
  }

  const handleSaveClicked = () => {
    const validationErrors = validatePodcastRequest(podcast);
    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForPodcast({
          podcast: podcast.id!,
          errors: validationErrors,
        }),
      );
    }

    const updatedPodcast = {
      ...podcast,
      assets: editState.assets,
      duration: editState.duration,
      voicePreset: editState.voicePreset!,
    };

    const request = preparePodcastRequest(podcast, updatedPodcast);

    if (_.keys(request).length === 0) {
      onMenuStateChange({
        ...menuState,
        secondaryAction: null,
      });

      return;
    }

    dispatch(
      updatePodcast({
        id: podcast.id!,
        request: request,
      }),
    );
  }

  const handleDiscardClicked = () => {
    onMenuStateChange({
      ...menuState,
      secondaryAction: null,
    });
  }

  const handleGenerateTranscriptClicked = () => {
    const validationErrors = validatePodcastRequest(podcast);
    if (_.size(validationErrors) > 0) {
      dispatch(
        updateValidationErrorsForPodcast({
          podcast: podcast.id!,
          errors: validationErrors,
        }),
      );
    }

    const updatedPodcast = {
      ...podcast,
      assets: editState.assets,
      duration: editState.duration,
      voicePreset: editState.voicePreset!,
    };

    const request = preparePodcastConfigurationRequest(podcast, updatedPodcast);
    dispatch(quickGeneratePodcast({
      id: podcast.id!,
      request: request,
      generateAudio: false,
    }));
  }

  const handleGenerateAudioClicked = () => {
    dispatch(generateAudioForPodcast(podcast.id!));
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBackClicked}>
          <i className="paperflite-left-arrow"></i>
          <img src="/images/coaching/studio_logo.png" alt="Studio Logo" />
        </button>
        <div className={styles.menubar}>
          <StudioMenuBar
            podcast={podcast}
            editState={editState}
            menuState={menuState}
            onMenuStateChange={onMenuStateChange}
            onSave={handleSaveClicked}
            onDiscard={handleDiscardClicked}
            onGenerateTranscript={handleGenerateTranscriptClicked}
            onGenerateAudio={handleGenerateAudioClicked}
          />
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.left}>
          <PodcastConfigurations podcast={podcast} value={editState} onChange={handleChange} />
        </div>
        <div className={styles.right}>
          <PodcastDetails podcast={podcast} />
        </div>
      </div>
    </div>
  );
};
